import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Alert, Autocomplete, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { EpisodicAttachableContainersQuery } from "generated-graphql/graphql";
import { useState } from "react";

import { CONTAINER_STATUS_LABELS } from "../Containers/containerDisplay";
import { EPISODIC_ATTACHABLE_CONTAINERS_QUERY, SET_CONTAINER_EPISODIC_EVENT_MUTATION } from "./api";

type ContainerOption = NonNullable<
  EpisodicAttachableContainersQuery["episodicEvent"]
>["attachableContainers"][number];

function containerOptionLabel(option: ContainerOption): string {
  const area = option.accumulationArea?.name ?? "No area";
  return `${option.containerLabel} · ${CONTAINER_STATUS_LABELS[option.status]} · ${area}`;
}

/** BAD_USER_INPUT errors (satellite area, pre-event or co-mingled waste,
 * inactive container, lapsed event) surface their server message verbatim;
 * anything else falls back to a generic toast. Exported for unit testing. */
export function describeAttachError(error: unknown, fallback: string): string {
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  const badUserInput = error.graphQLErrors.find((e) => e.extensions?.code === "BAD_USER_INPUT");
  return badUserInput?.message ?? fallback;
}

export type AttachContainerDialogProps = {
  eventId: string;
  open: boolean;
  onClose: () => void;
};

/**
 * An Autocomplete over the event's `attachableContainers` — the server
 * decides what is offered, mirroring its own attach guard — then a single
 * mutation call. Server-side rejections are shown verbatim and leave the
 * dialog open so the user can pick another container.
 */
export function AttachContainerDialog({ eventId, open, onClose }: AttachContainerDialogProps) {
  const alerts = useAlerts();
  const [selectedContainer, setSelectedContainer] = useState<ContainerOption | null>(null);

  const { data, loading: loadingOptions } = useQuery(EPISODIC_ATTACHABLE_CONTAINERS_QUERY, {
    variables: { eventId },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const containerOptions: ContainerOption[] = data?.episodicEvent?.attachableContainers ?? [];

  const [setContainerEpisodicEvent, { loading: attaching }] = useMutation(
    SET_CONTAINER_EPISODIC_EVENT_MUTATION,
    { refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"] }
  );

  const handleClose = () => {
    setSelectedContainer(null);
    onClose();
  };

  const handleAttach = async () => {
    if (!selectedContainer) {
      return;
    }
    await setContainerEpisodicEvent({
      variables: { containerId: selectedContainer.id, episodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container attached to event");
        handleClose();
      },
      onError: (error) => {
        alerts.error(describeAttachError(error, "An error occurred while attaching the container"));
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title="Attach existing container"
      primaryActionButtonProps={{
        loading: attaching,
        onClick: handleAttach,
        disabled: !selectedContainer,
        saveText: "Attach",
      }}
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Attached containers clock to the event&apos;s 60-day removal deadline. Only containers
          that can join are listed: active containers in a central area whose waste — every fill
          event — falls within the event&apos;s window (start date through the 60-day deadline).
          Waste added outside the window can&apos;t co-mingle under the event. Empty containers can
          always be attached.
        </Alert>
        <Autocomplete
          options={containerOptions}
          loading={loadingOptions}
          getOptionLabel={containerOptionLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedContainer}
          onChange={(_event, value) => setSelectedContainer(value)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {containerOptionLabel(option)}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Container" autoFocus />}
        />
      </Stack>
    </Dialog>
  );
}
